"use client"

import type React from "react"
import { TICKET_CATEGORIES } from "../../constants/ticket-categories"
import { type FormEvent, useState } from "react"
import { Spinner, Toast } from "react-bootstrap"
import { useTranslation } from "../../hooks/use-translation"
import { useNavigate } from "react-router-dom"
import { ImageIcon, ChevronDown, ArrowLeft } from "lucide-react"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { createTicket } from "../../redux/slices/ticketsSlice"
import { useAuth } from "../../contexts/auth-context"

export default function NewTicketPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { user } = useAuth()
  const { isLoading } = useAppSelector((state) => state.tickets)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [toastVariant, setToastVariant] = useState<"success" | "danger">("success")

  const [ticketData, setTicketData] = useState({
    type: "",
    title: "",
    description: "",
  })
  const [ticketImage, setTicketImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      setTicketImage(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!user) return

    const result = await dispatch(
      createTicket({
        type: ticketData.type,
        title: ticketData.title,
        description: ticketData.description,
        image: ticketImage,
      }),
    )

    // Check if the result has the expected structure
    if (result.payload && (result.payload as any).success) {
      setToastVariant("success")
      setToastMessage(t("ticketCreatedDescription"))
      setShowToast(true)
      setTimeout(() => navigate("/dashboard/tickets"), 1500)
    } else {
      setToastVariant("danger")
      setToastMessage(t("ticketCreationFailedDescription"))
      setShowToast(true)
    }
  }

  const selectedCategory = TICKET_CATEGORIES.find((cat) => cat.value === ticketData.type)

  return (
    <>
      <div
        className="d-md-none"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "70px",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          zIndex: 1030,
        }}
      >
        <div className="d-flex align-items-center h-100 px-3 gap-3">
          <button className="btn-back-circle flex-shrink-0" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-white mb-0 flex-grow-1" style={{ fontSize: "1.25rem", fontWeight: "600" }}>
            {t("createTicket")}
          </h1>
        </div>
      </div>

      <div className="d-md-none" style={{ paddingTop: "70px", padding: "70px 1rem 2rem 1rem" }}>
        <div className="container">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="text-white mb-3" style={{ fontSize: "1.5rem", fontWeight: "300" }}>
                {t("ticketType")}
              </label>
              <div className="position-relative">
                <select
                  className="form-control"
                  value={ticketData.type}
                  onChange={(e) => setTicketData({ ...ticketData, type: e.target.value })}
                  required
                  style={{
                    background: "white",
                    border: "none",
                    borderRadius: "16px",
                    padding: "1rem 1.5rem",
                    fontSize: "1.1rem",
                    minHeight: "60px",
                    appearance: "none",
                  }}
                >
                  <option value="">{t("selectTicketType")}</option>
                  {TICKET_CATEGORIES.map((category) => (
                    <option key={category.value} value={category.value}>
                      {t(category.key)}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={20}
                  style={{
                    position: "absolute",
                    right: "1.5rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#666",
                    pointerEvents: "none",
                  }}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="text-white mb-3" style={{ fontSize: "1.5rem", fontWeight: "300" }}>
                {t("title")}
              </label>
              <input
                type="text"
                className="form-control"
                value={ticketData.title}
                onChange={(e) => setTicketData({ ...ticketData, title: e.target.value })}
                placeholder={t("enterTitle")}
                required
                style={{
                  background: "white",
                  border: "none",
                  borderRadius: "16px",
                  padding: "1rem 1.5rem",
                  fontSize: "1.1rem",
                  minHeight: "60px",
                }}
              />
            </div>

            <div className="mb-4">
              <label className="text-white mb-3" style={{ fontSize: "1.5rem", fontWeight: "300" }}>
                {t("ticketDescription")}
              </label>
              <textarea
                className="form-control"
                rows={6}
                value={ticketData.description}
                onChange={(e) => setTicketData({ ...ticketData, description: e.target.value })}
                placeholder={t("ticketDescriptionPlaceholder")}
                required
                style={{
                  background: "white",
                  border: "none",
                  borderRadius: "16px",
                  padding: "1.5rem",
                  fontSize: "1.1rem",
                  resize: "none",
                }}
              />
            </div>

            <div className="mb-4">
              <label className="text-white mb-3" style={{ fontSize: "1.5rem", fontWeight: "300" }}>
                {t("uploadImage")}
              </label>
              <div className="d-flex align-items-center gap-3">
                <button
                  type="button"
                  onClick={() => document.getElementById("image")?.click()}
                  className="btn btn-transparent-outline d-flex align-items-center gap-2"
                >
                  <ImageIcon size={16} />
                  {t("selectImage")}
                </button>
                <input id="image" type="file" accept="image/*" className="d-none" onChange={handleImageChange} />
                {ticketImage && <span className="text-white small opacity-75">{ticketImage.name}</span>}
              </div>
              {imagePreview && (
                <div className="mt-3">
                  <img
                    src={imagePreview || "/placeholder.svg"}
                    alt="Preview"
                    className="img-fluid rounded"
                    style={{ maxHeight: "160px", borderRadius: "12px" }}
                  />
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end gap-3 mt-4">
              <button type="button" onClick={() => navigate(-1)} className="btn btn-cancel-outline">
                {t("cancel")}
              </button>
              <button type="submit" disabled={isLoading} className="btn btn-submit-white">
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    {t("submitting")}
                  </>
                ) : (
                  t("submitTicket")
                )}
              </button>
            </div>
          </form>

          <Toast
            show={showToast}
            onClose={() => setShowToast(false)}
            delay={3000}
            autohide
            className="position-fixed bottom-0 end-0 m-3"
            bg={toastVariant}
          >
            <Toast.Header>
              <strong className="me-auto">{toastVariant === "success" ? "Success" : "Error"}</strong>
            </Toast.Header>
            <Toast.Body className={toastVariant === "danger" ? "text-white" : undefined}>{toastMessage}</Toast.Body>
          </Toast>
        </div>
      </div>

      <div className="d-none d-md-block" style={{ padding: "2rem 1rem" }}>
        <div className="container">
          <div className="d-flex align-items-center gap-3 mb-4">
            <button onClick={() => navigate(-1)} className="btn-back-circle">
              <ArrowLeft size={20} />
            </button>
          </div>

          <h1 className="text-white mb-2" style={{ fontSize: "3rem", fontWeight: "bold" }}>
            {t("createTicket")}
          </h1>

          <h2 className="text-white mb-5" style={{ fontSize: "2rem", fontWeight: "300", opacity: 0.9 }}>
            {t("newTicket")}
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="text-white mb-3" style={{ fontSize: "1.5rem", fontWeight: "300" }}>
                {t("ticketType")}
              </label>
              <div className="position-relative">
                <select
                  className="form-control"
                  value={ticketData.type}
                  onChange={(e) => setTicketData({ ...ticketData, type: e.target.value })}
                  required
                  style={{
                    background: "white",
                    border: "none",
                    borderRadius: "16px",
                    padding: "1rem 1.5rem",
                    fontSize: "1.1rem",
                    minHeight: "60px",
                    appearance: "none",
                  }}
                >
                  <option value="">{t("selectTicketType")}</option>
                  {TICKET_CATEGORIES.map((category) => (
                    <option key={category.value} value={category.value}>
                      {t(category.key)}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={20}
                  style={{
                    position: "absolute",
                    right: "1.5rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#666",
                    pointerEvents: "none",
                  }}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="text-white mb-3" style={{ fontSize: "1.5rem", fontWeight: "300" }}>
                {t("title")}
              </label>
              <input
                type="text"
                className="form-control"
                value={ticketData.title}
                onChange={(e) => setTicketData({ ...ticketData, title: e.target.value })}
                placeholder={t("enterTitle")}
                required
                style={{
                  background: "white",
                  border: "none",
                  borderRadius: "16px",
                  padding: "1rem 1.5rem",
                  fontSize: "1.1rem",
                  minHeight: "60px",
                }}
              />
            </div>

            <div className="mb-4">
              <label className="text-white mb-3" style={{ fontSize: "1.5rem", fontWeight: "300" }}>
                {t("ticketDescription")}
              </label>
              <textarea
                className="form-control"
                rows={6}
                value={ticketData.description}
                onChange={(e) => setTicketData({ ...ticketData, description: e.target.value })}
                placeholder={t("ticketDescriptionPlaceholder")}
                required
                style={{
                  background: "white",
                  border: "none",
                  borderRadius: "16px",
                  padding: "1.5rem",
                  fontSize: "1.1rem",
                  resize: "none",
                }}
              />
            </div>

            <div className="mb-4">
              <label className="text-white mb-3" style={{ fontSize: "1.5rem", fontWeight: "300" }}>
                {t("uploadImage")}
              </label>
              <div className="d-flex align-items-center gap-3">
                <button
                  type="button"
                  onClick={() => document.getElementById("image")?.click()}
                  className="btn btn-transparent-outline d-flex align-items-center gap-2"
                >
                  <ImageIcon size={16} />
                  {t("selectImage")}
                </button>
                <input id="image" type="file" accept="image/*" className="d-none" onChange={handleImageChange} />
                {ticketImage && <span className="text-white small opacity-75">{ticketImage.name}</span>}
              </div>
              {imagePreview && (
                <div className="mt-3">
                  <img
                    src={imagePreview || "/placeholder.svg"}
                    alt="Preview"
                    className="img-fluid rounded"
                    style={{ maxHeight: "160px", borderRadius: "12px" }}
                  />
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end gap-3 mt-4">
              <button type="button" onClick={() => navigate(-1)} className="btn btn-cancel-outline">
                {t("cancel")}
              </button>
              <button type="submit" disabled={isLoading} className="btn btn-submit-white">
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    {t("submitting")}
                  </>
                ) : (
                  t("submitTicket")
                )}
              </button>
            </div>
          </form>

          <Toast
            show={showToast}
            onClose={() => setShowToast(false)}
            delay={3000}
            autohide
            className="position-fixed bottom-0 end-0 m-3"
            bg={toastVariant}
          >
            <Toast.Header>
              <strong className="me-auto">{toastVariant === "success" ? "Success" : "Error"}</strong>
            </Toast.Header>
            <Toast.Body className={toastVariant === "danger" ? "text-white" : undefined}>{toastMessage}</Toast.Body>
          </Toast>
        </div>
      </div>
    </>
  )
}
